<div class="ogc-upload-files ogc-upload-files--modal">
  @if (banner(); as b) {
    <div
      class="ogc-upload-files__banner"
      [class.ogc-upload-files__banner--success]="b.type === 'success'"
      [class.ogc-upload-files__banner--error]="b.type === 'error'"
      role="status"
    >
      <span>{{ b.message }}</span>
      <button type="button" class="ogc-upload-files__banner-close" (click)="dismissBanner()" aria-label="Dismiss">
        ×
      </button>
    </div>
  }

  <div class="step-content step-content--add-data-modal">
    <div class="ogc-upload-files__main">
      @if (processesLoading() || bucketsLoading()) {
        <p class="ogc-upload-files__status-lede" role="status">Loading upload configuration…</p>
      } @else if (processesLoadError()) {
        <p class="ogc-upload-files__status-lede ogc-upload-files__status-lede--error" role="alert">
          {{ processesLoadError() }}
        </p>
      }

      @if (existingGeoPackageLoading()) {
        <p class="ogc-upload-files__status-lede" role="status">Checking for an existing GeoPackage…</p>
      }
      @if (!existingGeoPackageLoading() && existingGeoPackage(); as existing) {
        <div class="selected-files-list">
          <div class="selected-files-header">
            <h4 class="selected-files-title">Existing GeoPackage</h4>
          </div>
          <div class="uploaded-files-container">
            <div class="uploaded-file-item completed">
              <div class="file-info">
                <div class="file-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M20 6L9 17L4 12" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <div class="file-details">
                  <span class="file-name">{{ existing.title || 'GeoPackage file' }}</span>
                  @if (existing.length) {
                    <span class="file-size">{{ getFileSize(existing.length) }}</span>
                  }
                </div>
              </div>
              <div class="file-actions">
                <span class="file-status status-completed">Uploaded</span>
              </div>
            </div>
          </div>
        </div>
      }

      @if (showOneTimeUploadNotice()) {
        <div class="ogc-upload-files__notice" role="alert">
          <span class="ogc-upload-files__notice-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 0 0 3.55 21H20.45A2 2 0 0 0 22.18 18L13.71 3.86A2 2 0 0 0 10.29 3.86Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span class="ogc-upload-files__notice-text">
            <strong>This upload is permanent.</strong> You can upload the GeoPackage for this dataset only once —
            it cannot be replaced, modified, or deleted afterwards. Please make sure the file is correct before uploading.
          </span>
        </div>
      }

      <div class="ogc-upload-files__storage">
        <label class="ogc-upload-files__storage-field">
          <span>Storage <strong>*</strong></span>
          <div class="ogc-upload-files__storage-dropdown">
            <button
              type="button"
              class="ogc-upload-files__storage-trigger"
              [class.ogc-upload-files__storage-trigger--open]="storageDropdownOpen()"
              [attr.aria-expanded]="storageDropdownOpen()"
              [disabled]="bucketsLoading() || buckets().length === 0 || uploading()"
              (click)="toggleStorageDropdown($event)"
            >
              @if (!selectedBucketId()) {
                <span class="ogc-upload-files__storage-placeholder">Select Storage bucket</span>
              } @else {
                <span class="ogc-upload-files__storage-value">{{ getSelectedBucketLabel() }}</span>
              }
              <svg
                class="ogc-upload-files__storage-chevron"
                [class.ogc-upload-files__storage-chevron--open]="storageDropdownOpen()"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            @if (storageDropdownOpen()) {
              <ul class="ogc-upload-files__storage-panel" role="listbox" (click)="$event.stopPropagation()">
                @for (bucket of buckets(); track bucketIdentifier(bucket)) {
                  <li class="ogc-upload-files__storage-item" role="presentation">
                    <button
                      type="button"
                      class="ogc-upload-files__storage-option"
                      role="option"
                      [class.ogc-upload-files__storage-option--active]="selectedBucketId() === bucketIdentifier(bucket)"
                      [attr.aria-selected]="selectedBucketId() === bucketIdentifier(bucket)"
                      (click)="selectStorageBucket(bucketIdentifier(bucket))"
                    >
                      {{ bucketIdentifier(bucket) }} ({{ bucket.readAccess }})
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
          @if (bucketsLoading()) {
            <small>Loading storage...</small>
          } @else if (buckets().length === 0) {
            <small>No private storage buckets were found.</small>
          }
        </label>
      </div>

      <div class="file-upload-section file-upload-section--modal-reflow">
        @if (showUploadPicker()) {
          <div class="file-upload-area file-upload-area--modal">
            <input
              #ogcFileInput
              type="file"
              id="ogc-gpkg-file-input"
              accept=".gpkg,application/geopackage+sqlite3"
              (change)="onFileSelected($event)"
              class="file-input"
              [disabled]="!canSelectFiles()"
            />
            <label
              for="ogc-gpkg-file-input"
              class="file-upload-label"
              [class.file-upload-label--drag-active]="isDropzoneActive()"
              [class.file-upload-label--disabled]="!canSelectFiles()"
              (dragover)="onDropZoneDragOver($event)"
              (dragenter)="onDropZoneDragEnter($event)"
              (dragleave)="onDropZoneDragLeave($event)"
              (drop)="onDropZoneDrop($event)"
            >
              <div class="file-upload-icon file-upload-icon--modal">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path d="M7 14L12 9L17 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M12 9V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <p class="file-upload-text file-upload-text--modal">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p class="file-upload-hint file-upload-hint--modal">
                Allowed: <strong class="file-upload-hint-fmt">{{ modalFormatLabel() }}</strong> (chosen file format)
              </p>
            </label>
            <button
              type="button"
              class="file-upload-browse-btn"
              [disabled]="!canSelectFiles()"
              (click)="openFilePicker(ogcFileInput)"
            >
              Browse files
            </button>
          </div>
        }

        @if (hasFilesToShow()) {
          <div class="selected-files-list" [class.selected-files-list--solo]="!showUploadPicker()">
            <div class="selected-files-header">
              <h4 class="selected-files-title">{{ selectedFilesTitle() }}</h4>
            </div>

            @if (uploadedFiles().length > 0) {
              @if (selectedFiles().length > 0) {
                <h5 class="section-subtitle">Uploaded Files ({{ uploadedFiles().length }})</h5>
              }
              <div class="uploaded-files-container">
                @for (fileItem of uploadedFiles(); track fileItem.file.name + '-' + fileItem.file.size) {
                  <div
                    class="uploaded-file-item"
                    [class.completed]="fileItem.status === 'completed'"
                    [class.error]="fileItem.status === 'error'"
                  >
                    <div class="file-info">
                      <div class="file-icon">
                        @if (fileItem.status !== 'completed') {
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path
                              d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                        } @else {
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M20 6L9 17L4 12" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                        }
                      </div>
                      <div class="file-details">
                        <span class="file-name">{{ fileItem.file.name }}</span>
                        <span class="file-size">{{ getFileSize(fileItem.file.size) }}</span>
                      </div>
                    </div>
                    <div class="file-actions">
                      <span
                        class="file-status"
                        [class.status-pending]="fileItem.status === 'pending'"
                        [class.status-uploading]="fileItem.status === 'uploading' || fileItem.status === 'onboarding'"
                        [class.status-completed]="fileItem.status === 'completed'"
                        [class.status-error]="fileItem.status === 'error'"
                      >
                        @if (fileItem.status === 'uploading' || fileItem.status === 'pending') {
                          {{ fileItem.progress }}%
                        } @else if (fileItem.status === 'onboarding') {
                          Registering…
                        } @else if (fileItem.status === 'completed') {
                          Done
                        } @else {
                          Failed
                        }
                      </span>
                      @if (fileItem.status !== 'completed') {
                        <button
                          type="button"
                          class="btn-icon btn-delete-file"
                          (click)="deleteUploadedFile(fileItem)"
                          title="Remove file"
                          [disabled]="uploading() && (fileItem.status === 'uploading' || fileItem.status === 'onboarding')"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path
                              d="M2 4H14M5.333 4V2.667C5.333 2.313 5.474 1.974 5.724 1.724C5.974 1.474 6.313 1.333 6.667 1.333H9.333C9.687 1.333 10.026 1.474 10.276 1.724C10.526 1.974 10.667 2.313 10.667 2.667V4M12.667 4V13.333C12.667 13.687 12.526 14.026 12.276 14.276C12.026 14.526 11.687 14.667 11.333 14.667H4.667C4.313 14.667 3.974 14.526 3.724 14.276C3.474 14.026 3.333 13.687 3.333 13.333V4H12.667Z"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </button>
                      }
                    </div>
                    @if (fileItem.status === 'uploading' || fileItem.status === 'onboarding') {
                      <div class="progress-bar">
                        <div class="progress-fill" [style.width.%]="fileItem.progress"></div>
                      </div>
                    }
                    @if (fileItem.error) {
                      <div class="file-error">{{ fileItem.error }}</div>
                    }
                  </div>
                }
              </div>
            }

            @if (selectedFiles().length > 0) {
              @if (uploadedFiles().length > 0) {
                <h5 class="section-subtitle">Newly Selected Files ({{ selectedFiles().length }})</h5>
              }
              <div class="selected-files-container">
                @for (file of selectedFiles(); track file.name + '-' + file.size + '-' + $index; let i = $index) {
                  <div class="selected-file-item">
                    <div class="file-info">
                      <div class="file-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path
                            d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </div>
                      <div class="file-details">
                        <span class="file-name">{{ file.name }}</span>
                        <span class="file-size">{{ getFileSize(file.size) }}</span>
                      </div>
                    </div>
                    <div class="file-actions-buttons">
                      <button
                        type="button"
                        class="btn-icon btn-upload"
                        (click)="uploadSingleFileFromSelected(i)"
                        title="Upload this file"
                        [disabled]="uploading() || processesLoading() || !processesReady() || !selectedBucketId()"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path
                            d="M14 10v2.667A1.333 1.333 0 0 1 12.667 14H3.333A1.333 1.333 0 0 1 2 12.667V10M11.333 5.333 8 2m0 0L4.667 5.333M8 2v8"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        class="btn-icon btn-delete-file"
                        (click)="removeSelectedFile(i)"
                        title="Delete this file"
                        [disabled]="uploading()"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path
                            d="M2 4H14M5.333 4V2.667C5.333 2.313 5.474 1.974 5.724 1.724C5.974 1.474 6.313 1.333 6.667 1.333H9.333C9.687 1.333 10.026 1.474 10.276 1.724C10.526 1.974 10.667 2.313 10.667 2.667V4M12.667 4V13.333C12.667 13.687 12.526 14.026 12.276 14.276C12.026 14.526 11.687 14.667 11.333 14.667H4.667C4.313 14.667 3.974 14.526 3.724 14.276C3.474 14.026 3.333 13.687 3.333 13.333V4H12.667Z"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    </div>

    <footer class="add-data-modal__footer">
      @if (showPreviousButton()) {
        <button type="button" class="btn-add-data btn-add-data--secondary" (click)="onModalPrevious()">
          ← Previous
        </button>
      }
      <div class="add-data-modal__footer-actions">
        @if (selectedFiles().length > 0) {
          <button
            type="button"
            class="btn-add-data btn-add-data--secondary"
            (click)="clearAllSelected()"
            [disabled]="uploading()"
          >
            {{ clearSelectedFilesLabel() }}
          </button>
        }
        <button
          type="button"
          class="btn-add-data btn-add-data--primary"
          [disabled]="!canFooterPrimary()"
          (click)="onFooterPrimaryClick()"
        >
          {{ footerPrimaryLabel() }}
        </button>
      </div>
    </footer>
  </div>
</div>
